import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { BestandteilQuelle, Report } from '../../types';
import { nachAusgabeBestandteile, type AusgabeFolge } from '../../services/bestandteile/bestandteilBuchung';
import { setzeBestandteilStunden } from '../../services/bestandteile/bestandteilService';
import {
    SPERRE_TEXT, abschlussSperre, bereinigeAusgabeIds, buchungsNutzer, fuehreAusgabenAus,
    ohneAusgabeHinweise, paketeFuerQuelle, schonAusgegebenHinweise, schrittNachAblage, speichereStunden,
    stundenAbfrageAus, stundenAbfragenZusammen, stundenEintragSchluessel, stundenErgebnisUebernehmen,
    stundenFensterOffen, stundenSpaeter,
    type AbschlussSperre, type StundenAbfrage,
} from './abschlussLogic';
import { bindeSchreibweg, projektDesVorgangs, verworfenText, type SchreibSperre } from './abschlussSchreibweg';
import { pruefeSchreiben } from './bestandteileDialogLogic';

/**
 * Der Bestandteil-Teil des Abschlussschritts — EIN Hook fuer alle sechs Module
 * (Plan B5, Faelle 2 und 3; die Regeln stehen in `abschlussLogic.ts`).
 *
 * Das Modul ruft `nachAusgabe(ausgabeId | ausgabeIds)`, sobald seine Ausgabe
 * geschrieben ist (Versand der Vorankuendigung, Freigabe der uebrigen). Der
 * Hook fragt dann je Ausgabe `nachAusgabeBestandteile` — IMMER mit der
 * Kennung der Ausgabe (ohne sie gaelte nur ein Zeitfenster) und mit
 * `holeBericht` — und haelt das Ergebnis fuer `BestandteilAbschlussHinweis`.
 *
 * ═══ KERNREGEL 7: NUR AN DER AUSLOESENDEN HANDLUNG ═══════════════════════
 * `nachAusgabe` laeuft nur aus dem Klick heraus, der die Ausgabe erzeugt hat
 * — nie beim Oeffnen. Ohne Paket dieser Quelle im Stand passiert NICHTS (kein
 * Netz, keine Meldung). Nur-Ansicht, Freelancer und ein Modul ohne
 * `onUpdate` bekommen keinen Schritt, nur den Satz dazu: die Paketposition
 * bucht dann der Nachtlauf (`scripts/integrity-sweep.ts`).
 *
 * ═══ DER STAND, DER JETZT GILT — EINE QUELLE JE MODUL ═════════════════════
 * Zwischen dem Klick und der Buchung liegen Ablage und Netz, und in A+S-Plan,
 * Handbuch und Baustellenordnung bucht das Modul seine EIGENE Position, waehrend
 * dieser Hook womoeglich gleichzeitig das Paket bucht. Beide muessen auf dem
 * Stand der jeweils anderen aufsetzen, sonst setzte die zweite Buchung die
 * erste zurueck (`App.handleUpdateReport` ersetzt, es wird nichts gemergt).
 * Deshalb haelt dieser Hook DIE eine Ref auf den juengsten Stand:
 *  - `holeBericht()` liest sie,
 *  - `schreibe(r)` legt `r` sofort hinein und reicht ihn an `onUpdate`,
 *  - ein neuer Bericht von aussen (Prop) ersetzt sie nur, wenn er ein ANDERES
 *    Objekt ist als der zuletzt gesehene — ein spaeter Render mit dem alten
 *    Prop ueberschreibt eine gerade geschriebene Buchung nicht.
 * Die Module buchen ihre eigene Position ueber `holeBericht`/`schreibe`.
 *
 * ═══ JEDER SCHREIBVORGANG IST AN SEIN PROJEKT GEBUNDEN (Codex R2b) ═══════
 * Die Projekt-Id wird beim START des Vorgangs festgehalten; vor JEDEM
 * Schreiben prueft `pruefeSchreiben` (dieselbe Regel wie im Hub, D):
 * eingehaengt, der aktuelle Stand gehoert zu diesem Projekt, der zu
 * schreibende auch. Sonst wird verworfen — der alte Schreibweg (Closure des
 * letzten Renders) setzte nach einem Projektwechsel den Bericht eines anderen
 * Projekts (Lehre spaetAendern in App.tsx). Eine faellige Paketbuchung holt
 * dann der Nachtlauf nach; die Meldung sagt es.
 *
 * Refs und Einhaenge-Merker werden in `useLayoutEffect` nachgefuehrt, nicht
 * in `useEffect`: Layout-Effekte laufen synchron im Commit, bevor eine
 * Netzantwort dazwischenkommen kann. Mit passiven Effekten gab es ein Fenster,
 * in dem eine endende Buchungsabfrage den alten Stand las bzw. nach dem
 * Aushaengen noch schrieb.
 *
 * ═══ STUNDEN-PFLICHTFENSTER (Stundenkontingent S3) ═══════════════════════
 * Hat die Ausgabe Bestandteile erledigt, haengt an `stand.stunden` die
 * Abfrage der Stunden (Regeln: `abschlussLogic`, Abschnitt „Stunden-
 * Pflichtfenster"). Der ZUSTAND wohnt hier, gezeichnet wird das Fenster an
 * GENAU einer Stelle je Modul (`<BestandteilStundenPflicht pflicht=
 * {abschluss.stundenPflicht} />`) — nie im Hinweis, den manche Module zweimal
 * rendern (Vorankuendigung, Baustellenordnung). Je Eintrag fragt der Hook
 * hoechstens einmal (`gefragt`); kein Fenster in Nur-Ansicht, fuer Freelancer
 * und wenn der Vorgang nicht mehr zum offenen Projekt gehoert.
 */

export interface AbschlussStand {
    laeuft: boolean;
    folge: AusgabeFolge | null;
    /** Saetze ohne Lauf: Sperre, keine Ausgabe festgehalten. */
    zusatz: string[];
    /** Die Stunden-Abfrage zu dieser Folge (S3) — `null`/fehlt: es fehlen keine Stunden. */
    stunden?: StundenAbfrage | null;
}

/** Was das Pflichtfenster braucht — `useBestandteilAbschluss().stundenPflicht`. */
export interface StundenPflicht {
    abfrage: StundenAbfrage | null;
    /** Speichert je Eintrag die Stunden (`eintragId → h`, schon gelesen). Wirft nie. */
    speichern: (eingaben: Readonly<Record<string, number>>) => Promise<void>;
    /** „Später nachtragen" — wirkt nur nach einem Fehlversuch (S3). */
    spaeter: () => void;
}

interface AbschlussArgs {
    report: Report | null | undefined;
    onUpdate?: ((r: Report) => void) | null;
    userProfile?: { id?: string | null; role?: string | null; first_name?: string | null; last_name?: string | null } | null;
    quelle: BestandteilQuelle;
    isReadOnly?: boolean;
    /** Projekt-id, wenn das Modul sie selbst kennt; sonst aus `report.metadata.globalData.projectId`. */
    projectId?: string | null;
}

const projektIdAus = (explizit: string | null | undefined, report: Report | null | undefined): string | null => {
    const id = (explizit || report?.metadata?.globalData?.projectId || '').trim();
    return id && id !== 'temp-id' ? id : null;
};

export const useBestandteilAbschluss = (args: AbschlussArgs) => {
    const { report, onUpdate, userProfile, quelle, isReadOnly = false, projectId } = args;

    /** Alles ausser dem Bericht: Schreibweg, Person, Quelle — je Commit nachgefuehrt. */
    const umgebung = useRef<Omit<AbschlussArgs, 'report'>>(args);
    useLayoutEffect(() => {
        umgebung.current = { onUpdate, userProfile, quelle, isReadOnly, projectId };
    }, [onUpdate, userProfile, quelle, isReadOnly, projectId]);

    /** Der juengste Stand (siehe Dateikopf) und der zuletzt gesehene Prop. */
    const bericht = useRef<Report | null>(report ?? null);
    const letzterProp = useRef<Report | null | undefined>(report);
    useLayoutEffect(() => {
        if (report === letzterProp.current) return;
        letzterProp.current = report;
        bericht.current = report ?? null;
    }, [report]);

    const eingehaengt = useRef(true);
    useLayoutEffect(() => {
        eingehaengt.current = true;
        return () => { eingehaengt.current = false; };
    }, []);

    /**
     * Der Stand der Meldung — zusaetzlich in einer Ref, damit die Rueckrufe
     * des Pflichtfensters (Speichern nach einer Netzrunde) auf dem JUENGSTEN
     * Stand aufsetzen, nicht auf dem ihres Renders.
     */
    const standRef = useRef<AbschlussStand | null>(null);
    const [stand, setStandRoh] = useState<AbschlussStand | null>(null);
    const setStand = useCallback((neu: AbschlussStand | null) => {
        standRef.current = neu;
        setStandRoh(neu);
    }, []);
    /**
     * Ein neuer Abschlussstand (Lauf beginnt, Sperre, Ergebnis …). Eine OFFENE
     * Stunden-Abfrage bleibt dabei erhalten und nimmt neue Eintraege auf
     * (`stundenAbfragenZusammen`) — ein zweiter, ueberlappender Lauf darf das
     * Pflichtfenster nie ohne Speichern verschwinden lassen (S3).
     */
    const setzeAbschluss = useCallback((neu: AbschlussStand) => {
        setStand({ ...neu, stunden: stundenAbfragenZusammen(standRef.current?.stunden, neu.stunden ?? null) });
    }, [setStand]);
    /** Eintraege, nach deren Stunden dieses Modul schon gefragt hat (Einmal-Schutz, S3). */
    const gefragt = useRef(new Set<string>());
    const abfrageZaehler = useRef(0);

    const services = report?.metadata?.globalData?.services;
    const pakete = useMemo(() => paketeFuerQuelle(services, quelle), [services, quelle]);
    const sperre: AbschlussSperre | null = abschlussSperre({
        isReadOnly, rolle: userProfile?.role ?? null, kannSchreiben: !!onUpdate,
    });

    /** Der Stand, der JETZT gilt — fuer jede Buchung dieses Moduls. */
    const holeBericht = useCallback((): Report | null => bericht.current, []);

    /** Ungeprueft schreiben: sofort in die Ref, dann ueber `onUpdate`. Nur ueber `bindeSchreibweg`. */
    const rohSchreiben = useCallback((r: Report) => {
        bericht.current = r;
        umgebung.current.onUpdate?.(r);
    }, []);

    /**
     * Schreibt `r` fuer das Projekt `projektId` — der Aufrufer haelt die Id
     * beim START seines Vorgangs fest (`projektDesVorgangs(holeBericht())`).
     * Liefert den Grund, wenn NICHT geschrieben wurde (ausgehaengt, Projekt
     * gewechselt, fremder Stand), sonst `null`.
     */
    const schreibeFuer = useCallback((projektId: string, r: Report): SchreibSperre | null =>
        bindeSchreibweg({
            projektId,
            istEingehaengt: () => eingehaengt.current,
            aktuell: () => bericht.current,
            schreibe: rohSchreiben,
            melde: s => console.warn(`[Abschlussschritt] Schreiben verworfen (${s}) — es wird nie in ein anderes Projekt geschrieben.`),
        })(r), [rohSchreiben]);

    /**
     * Nach einer Ausgabe: fragt nach, meldet, bucht die vollstaendig
     * gewordene Paketposition. Wirft nie. Gibt die Meldung zurueck (`null`,
     * wenn es keinen Schritt gab).
     */
    const nachAusgabe = useCallback(async (
        ids: ReadonlyArray<string | null | undefined> | string | null | undefined,
    ): Promise<AusgabeFolge | null> => {
        const jetzt = umgebung.current;
        const ausgang = bericht.current;
        const offen = paketeFuerQuelle(ausgang?.metadata?.globalData?.services, jetzt.quelle);
        // Kein Paket dieser Quelle: kein Schritt — auch kein Netz.
        if (!ausgang || offen.length === 0) return null;

        const gesperrt = abschlussSperre({
            isReadOnly: jetzt.isReadOnly, rolle: jetzt.userProfile?.role ?? null, kannSchreiben: !!jetzt.onUpdate,
        });
        if (gesperrt) {
            if (eingehaengt.current) setzeAbschluss({ laeuft: false, folge: null, zusatz: [SPERRE_TEXT[gesperrt]] });
            return null;
        }
        // Das Projekt des Vorgangs — beim START festgehalten. Nennt das Modul
        // ein anderes als der Stand, gibt es keinen Schritt.
        const pid = projektIdAus(jetzt.projectId, ausgang);
        if (!pid || pid !== projektDesVorgangs(ausgang)) return null;

        const liste = bereinigeAusgabeIds(ids);
        if (liste.length === 0) {
            if (eingehaengt.current) setzeAbschluss({ laeuft: false, folge: null, zusatz: ohneAusgabeHinweise(offen, jetzt.quelle) });
            return null;
        }

        if (eingehaengt.current) setzeAbschluss({ laeuft: true, folge: null, zusatz: [] });
        // Der an DIESES Projekt gebundene Schreibweg (siehe Dateikopf).
        // Ein Halter statt `let`: die Zuweisung geschieht im Rueckruf.
        const verworfen: { sperre: SchreibSperre | null } = { sperre: null };
        const schreibePaket = bindeSchreibweg({
            projektId: pid,
            istEingehaengt: () => eingehaengt.current,
            aktuell: () => bericht.current,
            schreibe: rohSchreiben,
            melde: s => {
                verworfen.sperre = s;
                console.warn(`[Abschlussschritt] Paketbuchung verworfen (${s}) — der Nachtlauf holt sie nach.`);
            },
        });
        /**
         * Der Stand fuer die Buchung: der jetzige, solange er zu diesem Projekt
         * gehoert. Sonst wirft er — bestandteilBuchung faellt dann auf den
         * Ausgangsstand zurueck, dessen Schreiben `schreibePaket` verwirft.
         */
        const holeGebunden = (): Report => {
            const jetztStand = bericht.current;
            const sperre = pruefeSchreiben({ eingehaengt: eingehaengt.current, projektId: pid, neu: jetztStand, aktuell: jetztStand });
            if (sperre || !jetztStand) throw new Error(`Schreibweg gesperrt (${sperre ?? 'ohne_stand'})`);
            return jetztStand;
        };
        const folge = await fuehreAusgabenAus(
            liste,
            ausgabeId => nachAusgabeBestandteile({
                report: ausgang,
                onUpdate: r => { schreibePaket(r); },
                user: buchungsNutzer(umgebung.current.userProfile),
                holeBericht: holeGebunden,
                projectId: pid,
                quelle: jetzt.quelle,
                ausgabeId,
            }),
            () => eingehaengt.current,
        );
        // Verworfen: eine gemeldete Buchung ist NICHT im Projekt angekommen.
        const sperre = verworfen.sperre;
        const ergebnis: AusgabeFolge = sperre ? { ...folge, gebucht: [] } : folge;
        if (eingehaengt.current) {
            // Stunden-Pflichtfenster (S3): nur, solange der Vorgang zum
            // offenen Projekt gehoert und die Person schreiben darf — sonst
            // bleibt es beim gelben Satz.
            const danach = umgebung.current;
            const fensterErlaubt = !sperre
                && !abschlussSperre({ isReadOnly: danach.isReadOnly, rolle: danach.userProfile?.role ?? null, kannSchreiben: !!danach.onUpdate })
                && !pruefeSchreiben({ eingehaengt: eingehaengt.current, projektId: pid, neu: bericht.current, aktuell: bericht.current });
            abfrageZaehler.current += 1;
            const stunden = stundenAbfrageAus(ergebnis, {
                id: abfrageZaehler.current, fensterErlaubt, bereitsGefragt: gefragt.current,
            });
            for (const z of stunden?.zeilen ?? []) gefragt.current.add(stundenEintragSchluessel(z.eintragId));
            setzeAbschluss({ laeuft: false, folge: ergebnis, zusatz: sperre ? [verworfenText(sperre, 'paket')] : [], stunden });
        }
        return ergebnis;
    }, [rohSchreiben, setzeAbschluss]);

    /**
     * Nach einer ABLAGE (Vorankuendigung, UfsA, SiGe-Plan) bzw. einem
     * FREIGABE-JOURNAL (A+S, Handbuch, Bauordnung): mit den Kennungen, die
     * dieser Vorgang geschrieben hat, wird nachgefragt und gebucht. Lagen nur
     * AELTERE Ausgaben der Fassung vor (`vorhandeneAusgabeId(s)`), wird nur
     * gemeldet — eine fremde Ausgabe ist nie Ausloeser dieser Handlung.
     */
    const nachAblage = useCallback(async (
        e: Parameters<typeof schrittNachAblage>[0],
    ): Promise<AusgabeFolge | null> => {
        const schritt = schrittNachAblage(e);
        if (schritt.art === 'nachfragen') return nachAusgabe(schritt.ausgabeIds);
        if (schritt.art === 'ohne_ausgabe') return nachAusgabe(null);
        const jetzt = umgebung.current;
        const offen = paketeFuerQuelle(bericht.current?.metadata?.globalData?.services, jetzt.quelle);
        if (offen.length === 0) return null;
        const gesperrt = abschlussSperre({
            isReadOnly: jetzt.isReadOnly, rolle: jetzt.userProfile?.role ?? null, kannSchreiben: !!jetzt.onUpdate,
        });
        if (eingehaengt.current) {
            setzeAbschluss({
                laeuft: false,
                folge: null,
                zusatz: gesperrt ? [SPERRE_TEXT[gesperrt]] : schonAusgegebenHinweise(offen, jetzt.quelle),
            });
        }
        return null;
    }, [nachAusgabe, setzeAbschluss]);

    /**
     * „Ausblenden" der Meldung. Eine offene Stunden-Abfrage bleibt (Pflicht,
     * S3) — nur die uebrigen Zeilen verschwinden.
     */
    const zuruecksetzen = useCallback(() => {
        const offen = standRef.current?.stunden;
        setStand(offen && stundenFensterOffen(offen) ? { laeuft: false, folge: null, zusatz: [], stunden: offen } : null);
    }, [setStand]);

    /** Pflichtfenster „Speichern": je Eintrag `setzeBestandteilStunden`, Ergebnis in die Abfrage. Wirft nie. */
    const stundenSpeichern = useCallback(async (eingaben: Readonly<Record<string, number>>): Promise<void> => {
        const vorher = standRef.current;
        const abfrage = vorher?.stunden;
        if (!vorher || !abfrage || abfrage.speichert || !stundenFensterOffen(abfrage)) return;
        setStand({ ...vorher, stunden: { ...abfrage, speichert: true } });
        const neu = await speichereStunden(abfrage, eingaben, setzeBestandteilStunden);
        if (!eingehaengt.current) return;
        const jetzt = standRef.current;
        // Eine neuere Abfrage hat inzwischen uebernommen — diese Antwort verfaellt.
        if (!jetzt?.stunden || jetzt.stunden.id !== abfrage.id) return;
        // Je Eintrag uebernehmen: ein ueberlappender Lauf kann waehrend des
        // Speicherns Zeilen ergaenzt haben — die bleiben offen stehen.
        setStand({ ...jetzt, stunden: stundenErgebnisUebernehmen(jetzt.stunden, neu) });
    }, [setStand]);

    /** Pflichtfenster „Später nachtragen" — nur nach einem Fehlversuch (S3). */
    const stundenSpaeterNachtragen = useCallback(() => {
        const jetzt = standRef.current;
        if (!jetzt?.stunden) return;
        const neu = stundenSpaeter(jetzt.stunden);
        if (neu !== jetzt.stunden) setStand({ ...jetzt, stunden: neu });
    }, [setStand]);

    const abfrage = stand?.stunden ?? null;
    const stundenPflicht = useMemo<StundenPflicht>(
        () => ({ abfrage, speichern: stundenSpeichern, spaeter: stundenSpaeterNachtragen }),
        [abfrage, stundenSpeichern, stundenSpaeterNachtragen],
    );

    return {
        /** Paketpositionen dieser Quelle im Stand der Sitzung (Fall 2/3). */
        pakete,
        /** Warum es keinen Schritt gibt — oder `null`. */
        sperre,
        stand,
        nachAusgabe,
        nachAblage,
        zuruecksetzen,
        /** Das Stunden-Pflichtfenster (S3) — an GENAU einer Stelle je Modul zeichnen: `<BestandteilStundenPflicht pflicht={…} />`. */
        stundenPflicht,
        /** Der juengste Stand des Moduls (Ref) — fuer die Buchung der eigenen Position. */
        holeBericht,
        /** Schreibt einen Stand fuer das beim Start festgehaltene Projekt — oder verwirft (Grund). */
        schreibeFuer,
    };
};
